import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Search,
  Plus,
  Eye,
  User,
  Users,
  Calendar,
  Briefcase,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

// Custom debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function ManageStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isSearching, setIsSearching] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Debounce search term - 500ms delay
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize
      };

      // Add status filter if not 'all'
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Add search term for server-side search
      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }

      const response = await adminAPI.getStudents(params);
      setStudents(response.students || []);

      // Set pagination info from response
      if (response.pagination) {
        setTotalStudents(response.pagination.total || 0);
        setTotalPages(response.pagination.totalPages || 1);
      }
    } catch (error) {
      toast.error('Failed to fetch students');
      console.error(error);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [currentPage, pageSize, statusFilter, debouncedSearchTerm]);

  // Fetch when pagination, filter, or debounced search changes
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Show searching indicator when search term changes but debounced value hasn't updated yet
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsSearching(true);
    }
  }, [searchTerm, debouncedSearchTerm]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    if (debouncedSearchTerm !== '') {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm]);

  const handleRowClick = (student) => {
    navigate(`/admin/students/${student.studentId}`);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(parseInt(newSize));
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Calculate stats from current page data
  const stats = {
    total: totalStudents,
    active: students.filter(s => s.onboardingCompleted).length,
    onboarding: students.filter(s => !s.onboardingCompleted).length
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="manage-students-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Manage Students</h1>
            <p className="text-gray-600">View and manage all registered students ({totalStudents} total)</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchStudents} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button data-testid="add-student-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Student
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="p-4 border-l-4 border-l-[#F2575F]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#F2575F]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{totalStudents}</p>
                  <p className="text-sm text-gray-600">
                    {debouncedSearchTerm ? 'Search Results' : 'Total Students'}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="p-4 border-l-4 border-l-green-500">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                  <p className="text-sm text-gray-600">Active (this page)</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="p-4 border-l-4 border-l-amber-500">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.onboarding}</p>
                  <p className="text-sm text-gray-600">Onboarding (this page)</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Search and Filter */}
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or agent name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
                data-testid="search-students-input"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              {isSearching && (
                <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F2575F] animate-spin" />
              )}
            </div>
            <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setCurrentPage(1); }}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Students</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 / page</SelectItem>
                <SelectItem value="20">20 / page</SelectItem>
                <SelectItem value="50">50 / page</SelectItem>
                <SelectItem value="100">100 / page</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Search indicator */}
          {debouncedSearchTerm && (
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="secondary" className="bg-[#F2575F]/10 text-[#F2575F]">
                <Search className="w-3 h-3 mr-1" />
                Searching: "{debouncedSearchTerm}"
              </Badge>
              <span className="text-sm text-gray-500">
                {totalStudents} result{totalStudents !== 1 ? 's' : ''} found
              </span>
            </div>
          )}
        </Card>

        {/* Students Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#F2575F] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : students.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Users className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-lg font-medium">No students found</p>
              <p className="text-sm">
                {debouncedSearchTerm
                  ? `No results for "${debouncedSearchTerm}". Try a different search term.`
                  : 'Try adjusting your filter criteria'
                }
              </p>
              {debouncedSearchTerm && (
                <Button variant="outline" size="sm" className="mt-4" onClick={handleClearSearch}>
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="students-table">
                <thead className="bg-stone-50/80 border-b border-stone-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Student</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned Agent</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned Counselor</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Services</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {students.map((student, index) => (
                    <motion.tr
                      key={student.studentId || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="hover:bg-stone-50/80 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(student)}
                      data-testid={`student-row-${student.studentId}`}
                    >
                      {/* Student Name & Email */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-[#F2575F] to-[#E04850] rounded-full flex items-center justify-center text-white font-semibold">
                            {student.user?.firstName?.[0]}{student.user?.lastName?.[0]}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{student.user?.firstName} {student.user?.lastName}</p>
                            <p className="text-xs text-gray-500">{student.user?.email}</p>
                          </div>
                        </div>
                      </td>

                      {/* Assigned Agent */}
                      <td className="px-6 py-4">
                        {student.assignedAgent && student.assignedAgent.firstName ? (
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-medium text-xs">
                              {student.assignedAgent.firstName?.[0]}{student.assignedAgent.lastName?.[0]}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-700">
                                {student.assignedAgent.firstName} {student.assignedAgent.lastName}
                              </p>
                              {student.assignedAgent.email && (
                                <p className="text-xs text-gray-400">{student.assignedAgent.email}</p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <Badge variant="outline" className="bg-stone-50 text-gray-500 border-stone-200">
                            <User className="w-3 h-3 mr-1" />
                            Unassigned
                          </Badge>
                        )}
                      </td>

                      {/* Assigned Counselor */}
                      <td className="px-6 py-4">
                        {student.assignedCounselor && student.assignedCounselor.firstName ? (
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-medium text-xs">
                              {student.assignedCounselor.firstName?.[0]}{student.assignedCounselor.lastName?.[0]}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-700">
                                {student.assignedCounselor.firstName} {student.assignedCounselor.lastName}
                              </p>
                              {student.assignedCounselor.email && (
                                <p className="text-xs text-gray-400">{student.assignedCounselor.email}</p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <Badge variant="outline" className="bg-stone-50 text-gray-500 border-stone-200">
                            <Users className="w-3 h-3 mr-1" />
                            Unassigned
                          </Badge>
                        )}
                      </td>

                      {/* Services Count */}
                      <td className="px-6 py-4">
                        <Badge className="bg-[#F2575F]/10 text-[#F2575F]">
                          <Briefcase className="w-3 h-3 mr-1" />
                          {student.serviceRequests?.length || 0} services
                        </Badge>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        <Badge className={student.onboardingCompleted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {student.onboardingCompleted ? 'Active' : 'Onboarding'}
                        </Badge>
                      </td>

                      {/* Created Date */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {student.createdAt ? formatDistanceToNow(new Date(student.createdAt), { addSuffix: true }) : '-'}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRowClick(student)}
                            data-testid={`view-student-${index}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Pagination Controls */}
        {!loading && totalStudents > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalStudents)} of {totalStudents} students
              {debouncedSearchTerm && <span className="text-[#F2575F] ml-1">(filtered)</span>}
            </div>

            <div className="flex items-center gap-2">
              {/* First Page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>

              {/* Previous Page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-2 text-gray-400">...</span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className="min-w-[36px]"
                    >
                      {page}
                    </Button>
                  )
                ))}
              </div>

              {/* Next Page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>

              {/* Last Page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
